import { Models, PostRequest } from "api/types";
import { PersonalisationData } from "modules/personalisation/context/PersonalisationDataContext";

// API types from backend
type SavePreferencesRequest = PostRequest<"/user/preferences">;
type GetPreferencesResponse = {
  journeyExperience?: Models<"JourneyExperience"> | null;
  journeyExperienceComment?: string | null;
  ethnicBackground?: Models<"EthnicBackground"> | null;
  skinTone?: Models<"SkinTone"> | null;
  sunResponse?: Models<"SunResponse"> | null;
  skinType?: Models<"SkinType"> | null;
  aiPersona?: Models<"AIPersona"> | null;
  concernIds?: string[] | null;
};

// Single mapping table - source of truth
const FIELD_MAPPINGS = {
  journey: {
    beginner: "JustBeginning",
    intermediate: "ExploringFurther",
    advanced: "ContinuingJourney",
  },
  skinColor: {
    "very-fair": "VeryFair",
    "fair-light": "FairLight",
    "medium-olive": "MediumOlive",
    "tan-light-brown": "TanLightBrown",
    "brown": "TanLightBrown",
    "deep-brown-black": "DeepBrownToBlack",
  },
  skinSun: {
    "highly-sensitive": "BurnsEasilyRarelyTans",
    "often-sensitive": "BurnsSometimesUsuallyTans",
    "usually-tan": "RarelyBurnsTansEasily",
    "rarely-sensitive": "RarelyBurnsTansEasily",
    "resilient": "AlmostNeverBurnsDeeplyPigmented",
  },
  skinBalance: {
    "very-dry": "VeryDry",
    "mostly-dry": "MostlyDry",
    "combination": "Combination",
    "mostly-oily": "MostlyOily",
    "balanced": "Balanced",
  },
  advisor: {
    adonis: "Adonis",
    aria: "Aria",
  },
} as const;

// Generic mapping helpers
const mapToBackend = (value: string, field: keyof typeof FIELD_MAPPINGS) =>
  FIELD_MAPPINGS[field][value as keyof (typeof FIELD_MAPPINGS)[typeof field]];

const mapFromBackend = (value: string, field: keyof typeof FIELD_MAPPINGS) =>
  Object.entries(FIELD_MAPPINGS[field]).find(([_, v]) => v === value)?.[0];

// Convert frontend data to backend save request
export const toSaveRequest = (data: PersonalisationData): SavePreferencesRequest => ({
  journeyExperience:
    data.journey.state === "answered"
      ? (mapToBackend(data.journey.value, "journey") as Models<"JourneyExperience">)
      : undefined,
  journeyExperienceComment:
    data.journeyDetails.state === "answered" ? data.journeyDetails.value : undefined,
  ethnicBackground:
    data.heritage.state === "answered"
      ? (data.heritage.value as Models<"EthnicBackground">)
      : undefined,
  skinTone:
    data.skinColor.state === "answered"
      ? (mapToBackend(data.skinColor.value, "skinColor") as Models<"SkinTone">)
      : undefined,
  sunResponse:
    data.skinSun.state === "answered"
      ? (mapToBackend(data.skinSun.value, "skinSun") as Models<"SunResponse">)
      : undefined,
  skinType:
    data.skinBalance.state === "answered"
      ? (mapToBackend(data.skinBalance.value, "skinBalance") as Models<"SkinType">)
      : undefined,
  aiPersona:
    data.advisor.state === "answered"
      ? (mapToBackend(data.advisor.value.id, "advisor") as Models<"AIPersona">)
      : undefined,
  concernIds: data.concerns.state === "answered" ? data.concerns.value : undefined,
});

// Convert backend response to frontend data
export const fromSavedPreferences = (backendData: GetPreferencesResponse): PersonalisationData => ({
  advisor: !backendData.aiPersona
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: {
          id: mapFromBackend(backendData.aiPersona, "advisor") || "adonis",
          label: backendData.aiPersona,
          avatarUrl: "", // Set by context
        },
      },
  concerns:
    !backendData.concernIds || backendData.concernIds.length === 0
      ? { state: "unanswered" }
      : {
          state: "answered",
          value: backendData.concernIds,
        },
  journey: !backendData.journeyExperience
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: mapFromBackend(backendData.journeyExperience, "journey") || "beginner",
      },
  journeyDetails: !backendData.journeyExperienceComment
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: backendData.journeyExperienceComment,
      },
  heritage: !backendData.ethnicBackground
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: backendData.ethnicBackground,
      },
  skinColor: !backendData.skinTone
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: mapFromBackend(backendData.skinTone, "skinColor") || "fair-light",
      },
  skinSun: !backendData.sunResponse
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: mapFromBackend(backendData.sunResponse, "skinSun") || "often-sensitive",
      },
  skinBalance: !backendData.skinType
    ? { state: "unanswered" }
    : {
        state: "answered",
        value: mapFromBackend(backendData.skinType, "skinBalance") || "combination",
      },
});
